import React, {Component} from "react";
import {Control, Field, Icon} from "bloomer";
import Card from "../../../elements/Card";
import Button from "../../../elements/Button";
import SendGAEvent, {GAEventActions, GAEventModels} from "../../../utils/SendGAEvent";
import ErrorUtils from "../../../utils/ErrorUtils";
import {toast} from "react-toastify";
import SectionName from "../../../elements/SectionName";
import Spacer from "../../../elements/Spacer";
import ModelContentContainer from "../../../elements/ModelContentContainer";
import {initText2ArtJob, collectText2ArtJob} from "../../../api/models/Text2Art";

interface IText2ArtModelState {
    loading: boolean;
    awaiting: boolean;
    loadingEstimate: number;
    textPrompt: any;
    requestedJobId: string;
    imageBase64: string;
    errorMessage: string;
}

class Text2ArtModel extends Component<{}, IText2ArtModelState> {
    public inputRef: any = null;
    public loadingInterval: any = null;

    public inputStyle = {
        width: '100%'
    };

    public constructor(props: {}) {
        super(props);

        this.state = {
            loading: false,
            awaiting: false,
            loadingEstimate: null,
            textPrompt: "",
            requestedJobId: "",
            imageBase64: "",
            errorMessage: "",
        };
    }

    public componentDidMount() {
        SendGAEvent(GAEventActions.VIEW_MODEL, GAEventModels.TEXT2ART);
    }

    public onSubmit = async (e: any) => {
        e.target.blur();
        this.inputRef.blur();
        clearInterval(this.loadingInterval);

        this.setState(prevState => ({
            requestedJobId: "",
            textPrompt: this.inputRef.value,
            loading: true,
            loadingEstimate: Math.ceil(80 + Math.random()*2), // Normally takes around 1 minute
            errorMessage: ""
        }));

        let errorOccurred = false;

        this.loadingInterval = setInterval(() => {
            this.setState(prevState => ({
                loadingEstimate: prevState.loadingEstimate - 1,
            }) );
            if (this.state.loadingEstimate % 5 === 0 && !this.state.awaiting) { // Only call collectJob every 2 seconds to limit requests.
                this.collectJob();
            }
        }, 1000);


        const response = await initText2ArtJob(this.inputRef.value).catch((error) => {
            if (ErrorUtils.requestLimitExceeded(error.response)) {
                this.setState({loading: false});
                toast.error(ErrorUtils.messageContent.REQUEST_LIMIT_EXCEEDED);
            } else {
                this.setState({
                    loading: false,
                    errorMessage: "Couldn't process generation."
                });
            }
            errorOccurred = true;
        });

        if (errorOccurred) {
            return;
        }

        if (response && response.data) {
            this.setState({
                requestedJobId: response.data
            });
        }

        SendGAEvent(GAEventActions.CALL_MODEL, GAEventModels.TEXT2ART);
    };

    // Called on interval after submit button is pressed.
    // This functions polls the backend to see if the previously requested job is yet complete.
    public collectJob = async () => {
        if (!this.state.requestedJobId) {
            return;
        }

        this.setState({ awaiting: true });
        const response = await collectText2ArtJob(this.state.requestedJobId).catch((error) => {
            if (ErrorUtils.requestLimitExceeded(error.response)) {
                this.setState({loading: false, awaiting: false });
                toast.error(ErrorUtils.messageContent.REQUEST_LIMIT_EXCEEDED);
            } else {
                this.setState({
                    loading: false,
                    awaiting: false,
                    errorMessage: "Couldn't process generation."
                });
            }
            return; // Return on error
        });

        if (response && response.data) {
            if (response.headers["content-length"] === "0") {
                this.setState({ awaiting: false });
                return;
            }

            if (this.state.loading) { // Don't update if user already pressed the reset button
                // Check if we received the completed header, and clear the loading interval if so.
                const key = "completed";
                if (response.headers[key] === "True") {
                    clearInterval(this.loadingInterval);
                }

                const reader = new FileReader();
                reader.addEventListener("load", () => {
                    this.setState({
                        loading: response.headers[key] === "False",
                        awaiting: false,
                        imageBase64: reader.result as string,
                    });
                }, false);
                reader.readAsDataURL(response.data);
            }
        } else {
            this.setState({
                loading: false,
                errorMessage: "Couldn't process generation."
            });
        }
    };

    public resetState = async (e: any) => {
        e.target.blur();
        clearInterval(this.loadingInterval);

        this.setState({
            loading: false,
            loadingEstimate: null,
            imageBase64: "",
            errorMessage: "",
        });
    };

    public getLoadingDurationEstimateText = () => {
        if (this.state.loadingEstimate < 1) {
            return "";
        }
        const mins = Math.floor(this.state.loadingEstimate / 60);
        const secs = this.state.loadingEstimate % 60;
        return "" + (mins ? mins + " minute" + (mins === 1 ? " " : "s ") : "")
                  + (secs ? secs + " second" + (secs === 1 ? " " : "s") : "")
    }

    public componentWillUnmount() {
        clearInterval(this.loadingInterval);
    }

    public render() {
        return (
            <React.Fragment>
                <ModelContentContainer>
                    <Card>
                        <Field>
                            <Control>
                                <div>
                                    <p>
                                        This API allows you to generate artistic images from a supplied text prompt using
                                        state of the art generative model technology. Simply submit a text prompt like "a
                                        painting of a robot battle in the sky" and the AI will attempt to generate an
                                        image which fits that description.
                                    </p>
                                    <SectionName>Enter text prompt:</SectionName>
                                    <input
                                        ref={(el) => this.inputRef = el}
                                        type="text"
                                        style={this.inputStyle}
                                    />
                                </div>
                            </Control>
                        </Field>
                        <Spacer/>
                        <div>
                            <Button onClick={this.onSubmit} disabled={this.state.loading || this.state.imageBase64.length > 0}>Submit</Button>
                            <Button onClick={this.resetState} style={{marginLeft: "5px"}}>Reset</Button>
                        </div>
                        { this.state.imageBase64 && this.state.loading ?
                            <div> Intermediate image produced by AI: </div> : <></>
                        }
                        { this.state.imageBase64 && !this.state.loading ?
                            <div> Result image produced by AI: </div> : <></>
                        }
                        {
                            this.state.loading && <div>
                                <Spacer/>
                                <span>Loading...</span>
                                <Icon className="fas fa-spinner fa-spin"/>
                                <div>Estimated time remaining: {this.getLoadingDurationEstimateText()}</div>
                            </div>
                        }
                        {  this.state.imageBase64 ?
                            <div>
                                <img
                                    alt="Result image"
                                    src={this.state.imageBase64}
                                />
                                <div>
                                    <a download="generatedImage.png" href={this.state.imageBase64}>
                                        <Button style={{marginBottom: "7px", width: "100%"}}>Save</Button>
                                    </a>
                                </div>
                            </div> : <></>
                        }
                        <Spacer/>
                        {
                            !this.state.loading && this.state.errorMessage ?
                                <div>{this.state.errorMessage}</div> : <></>
                        }
                        <Spacer/>
                    </Card>
                </ModelContentContainer>
            </React.Fragment>
        );
    }
}

export default Text2ArtModel;



// WEBPACK FOOTER //
// ./src/routes/Dashboard/Text2Art/Text2ArtModel.tsx